import { mapState } from 'vuex';
import ApiService from '@/services/apiService';
import WGS84Service from '@/services/wgs84ProjectionService';
import { logMessage } from '@/sentry';
import get from 'lodash/get';
import set from 'lodash/set';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { getMapOverlayData } from '@/utils/geoMapOverlayInfo';

const defaultItemsPerPage = 50;
const defaultTableOptions = {
  page: 1,
  itemsPerPage: defaultItemsPerPage,
  itemsLength: -1,
};

export default {
  props: {
    // the main-view shares the same Geodata-mixin as the `geoDetails` component,
    // so there is no need to perform redundant data-fetching,
    // only fetch stuff in the main-view and hand that instance over here to `geoDetails`,
    // which then shall not perform its own fetching
    dataInstance: {
      type: Object,
      required: false,
      default: () => null,
    },
  },
  data: function () {
    return {
      apiGeoHistoryFetchMethod: 'digitalTwinFetchGeoHistory',

      geoPolygonJsonString: '',

      mapMode: 'default',
      mapHistoryQueryStartDate: new Date(new Date() - 86400 * 7 * 1000),
      mapHistoryQueryEndDate: new Date(),
      mapHistoryCoordinatePath: null,
      mapHistoryCoordinates: null,
      mapHistoryTotalCount: -1,
      geoHistoryPaginationOptions: defaultTableOptions,

      mapHistoryLastUpdate: null,
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'color']),

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    addressObject() {
      return {
        address: this.twinObject.data.address,
        coordinates: [
          get(this.twinObject, 'coordinates.coordinates[0]') || null,
          get(this.twinObject, 'coordinates.coordinates[1]') || null,
        ],
        polygon: get(this.twinObject, 'polygon.coordinates') || [],
      };
    },
    dtwinPolygon() {
      return get(this.twinObject, 'polygon.coordinates', []);
    },
    dtwinCoordinates() {
      return get(this.twinObject, 'coordinates.coordinates', [null, null]);
    },
    dtwinOverlayInfo() {
      return getMapOverlayData(get(this.floorTwinOverlayData, 'data.indoor') || get(this.twinObject, 'data.indoor'));
    },
    showMap() {
      if (get(this.twinObject, 'coordinates.coordinates[1]')) {
        return this.hasValidCoordinate;
      }
      return false;
    },
    showHeaderMap() {
      if (get(this.twinObject, 'data.twinImagePreferOverMap') && get(this.twinObject, 'data.twinImageId')) {
        return false;
      }
      return this.showMap;
    },
    hasValidCoordinate() {
      const coordinate = {
        lat: this.dtwinCoordinates[1],
        lon: this.dtwinCoordinates[0],
      };
      return WGS84Service.isValidWgs84(coordinate);
    },
    isFloorTwin() {
      return this.twinObject.data.type === 'floor';
    },
    addressPickerCoordinateLookup() {
      return get(this.twinObject, 'data.coordinateMode') !== 'manual';
    },
    dtwinHistoryZones() {
      return get(this.mapHistoryLastUpdate, 'properties.zones') || [];
    },
    geoHistoryPaginationProps() {
      const { page, itemsPerPage } = this.geoHistoryPaginationOptions;
      const usedPage = page ? page - 1 : 0;
      const usedItemsPerPage = itemsPerPage ? itemsPerPage : defaultItemsPerPage;

      return {
        itemsPerPageText: this.$i18n_t('DigitalTwin.DigitalTwin.map.historyItemsPerPage'),
        itemsPerPageOptions: [10, 20, defaultItemsPerPage],
        pagination: {
          page: usedPage,
          itemsPerPage: this.geoHistoryPaginationOptions.itemsPerPage,
          pageStart: usedPage * usedItemsPerPage,
          pageStop: usedPage * usedItemsPerPage + usedItemsPerPage,
          itemsLength: this.mapHistoryTotalCount,
        },
      };
    },
  },
  watch: {
    $route() {
      this.mapHistoryFetchLastUpdate();
      this.setupGeoData();
    },
    geoHistoryPaginationOptions: {
      handler() {
        this.mapHistoryFetch();
      },
      deep: true,
    },
    'twinObject.polygon': {
      handler() {
        this.setupGeoData();
      },
    },
    'twinObject.data.polygonRadius': {
      handler() {
        if (this.twinObject.data.polygonMode === 'auto') {
          this.dtwinCoordinateUpdated({
            lon: get(this.twinObject, 'coordinates.coordinates[0]'),
            lat: get(this.twinObject, 'coordinates.coordinates[1]'),
          });
        }
      },
    },
    // if data is shared between e.g. geoDetails and the main-view, listen to history updates
    'dataInstance.mapHistoryLastUpdate': {
      handler() {
        this.mapHistoryLastUpdate = this.dataInstance.mapHistoryLastUpdate;
      },
    },
  },
  mounted() {
    this.mapHistoryFetchLastUpdate();
    this.setupGeoData();
  },
  methods: {
    setupGeoData() {
      this.geoPolygonJsonString = JSON.stringify(get(this.twinObject, 'polygon.coordinates[0]') || []);
      this.twinObject.data.polygonMode =
        this.twinObject.data.polygonMode || (!get(this.twinObject, 'polygon.coordinates[0][0]') ? 'auto' : 'manual');
      this.twinObject.data.polygonRadius = this.twinObject.data.polygonRadius || 5;
    },
    floorTwinFilter(twin) {
      // NOTE: there was a `&& twin.polygon` check as well, but since the simple fetch-endpoint
      // omits that information in the `extractBareTwin` method, leave it out.
      // the check/requirement is not entirely necessary but just a nice to have.
      return twin.data.type === 'floor';
    },
    addressSelected(addressData) {
      set(this.twinObject, 'data.address', {});
      this.twinObject.data.address.formatted_address = addressData.address;
      this.twinObject.data.coordinateMode = addressData.pickedManualCoordinates ? 'manual' : 'auto';
      this.dtwinCoordinateUpdated({ lat: addressData.coordinates[1], lon: addressData.coordinates[0] });
      this.twinObject.data = cloneDeep(this.twinObject.data);
    },

    dtwinGeopolygonUpdated(e, mode = 'manual') {
      this.dtwinGeoPolygonEdited(e ? JSON.stringify(e) : null, mode);
    },
    dtwinGeoPolygonEdited(e, mode = 'manual') {
      let parsed = null;
      try {
        parsed = JSON.parse(e);
        // eslint-disable-next-line no-empty
      } catch (err) {}
      if (!e || e.trim() === '' || (Array.isArray(parsed) && parsed.length === 0)) {
        this.twinObject.polygon = { coordinates: [] };
      } else if (parsed && Array.isArray(parsed) && parsed.length > 0) {
        // make sure only valid stuff was entered
        let polygonCoordinates = parsed
          .map((c) => {
            if (Array.isArray(c) && c.length >= 2) {
              if (!isNaN(parseFloat(c[0])) && !isNaN(parseFloat(c[1]))) {
                return [parseFloat(c[0]), parseFloat(c[1])];
              }
            }
            return null;
          })
          .filter((c) => c);
        if (polygonCoordinates.length >= 3) {
          if (
            polygonCoordinates[0][0] !== polygonCoordinates[polygonCoordinates.length - 1][0] ||
            polygonCoordinates[0][1] !== polygonCoordinates[polygonCoordinates.length - 1][1]
          ) {
            polygonCoordinates.push(polygonCoordinates[0]);
          }
          this.twinObject.polygon = { coordinates: [polygonCoordinates] };
        }
      }

      this.twinObject.data.polygonMode = !get(this.twinObject, 'polygon.coordinates[0][0]') ? 'auto' : mode;
      this.twinObject.data.polygonRadius = this.twinObject.data.polygonRadius || 5;
      this.geoPolygonJsonString = JSON.stringify(get(this.twinObject, 'polygon.coordinates[0]', []));
      if (!get(this.twinObject, 'polygon.coordinates[0][0]') && get(this.twinObject, 'coordinates.coordinates[1]')) {
        this.dtwinCoordinateUpdated({
          lat: this.twinObject.coordinates.coordinates[1],
          lon: this.twinObject.coordinates.coordinates[0],
        });
      }
    },

    dtwinCoordinateUpdated(e) {
      if (get(e, 'lat')) {
        const hadCoordinatesYet = this.showMap;
        this.twinObject.coordinates = { coordinates: [e.lon, e.lat] };
        if (
          this.showMap &&
          ((!hadCoordinatesYet && !get(this.twinObject, 'polygon.coordinates[0][0]')) ||
            this.twinObject.data.polygonMode === 'auto')
        ) {
          const radius = this.twinObject.data.polygonRadius || 5;

          // the inner angles of hexagons is 60 degrees
          // from the center, go negative 30 degrees (half 60) to the first corner
          let corners = [WGS84Service.wgs84FromWithBearingAndDistance(e, (330 * Math.PI) / 180, radius)];
          for (let i = 0; i < 5; i++) {
            // it's a hexagon, so go round 5 metres in plus 60 degrees from the last corner, starting at 90 deg from the first
            corners.push(
              WGS84Service.wgs84FromWithBearingAndDistance(corners[i], ((60 * i + 90) * Math.PI) / 180, radius)
            );
          }
          const coordinates = corners.map((c) => {
            return [c.lon, c.lat];
          });
          this.dtwinGeopolygonUpdated(coordinates, 'auto');
        }
      }
    },

    mapHistoryFetchLastUpdate() {
      if (!this.dataInstance) {
        ApiService[this.apiGeoHistoryFetchMethod](this.$route.params.id, null, null, 0, 1)
          .then((res) => {
            this.mapHistoryLastUpdate = get(res, 'history[0]');
          })
          .catch((error) => {
            console.error(error);
          });
      } else {
        this.mapHistoryLastUpdate = this.dataInstance.mapHistoryLastUpdate;
      }
    },
    mapHistoryRangePicked(dateObject) {
      this.mapHistoryQueryStartDate = new Date(dateObject.from * 1000);
      this.mapHistoryQueryEndDate = new Date(dateObject.to * 1000);
      if (isEqual(this.geoHistoryPaginationOptions, defaultTableOptions)) {
        this.mapHistoryFetch();
      } else {
        this.geoHistoryPaginationOptions = defaultTableOptions;
      }
    },
    mapHistoryFetch() {
      const { page, itemsPerPage } = this.geoHistoryPaginationOptions;

      const usedPage = page ? page - 1 : 0;
      const usedItemsPerPage = itemsPerPage ? itemsPerPage : 50;

      ApiService[this.apiGeoHistoryFetchMethod](
        this.twinObject.id,
        this.mapHistoryQueryStartDate,
        this.mapHistoryQueryEndDate,
        usedPage * usedItemsPerPage,
        usedItemsPerPage
      )
        .then((res) => {
          this.mapHistoryCoordinates = res.history.reverse();
          this.mapHistoryTotalCount = res.totalCount;
          this.mapHistoryCoordinatePath = res.history.map((h) => get(h, 'coordinates.coordinates'));
        })
        .catch((error) => {
          logMessage(`Twin states failed to load. ${error.toString()}`);
          this.$snack(this.$i18n_t('Common.digitalTwins.errorLoadingStates'), 'error');
        });
    },
    addressClear() {
      this.twinObject.data.address = {};
      this.twinObject.coordinates = {};
      set(this.twinObject, 'data.coordinateMode', 'auto');
    },
  },
};
